<template>
  <section>
    <div
      class="mt-5 border-style rounded-lg flex items-center justify-center py-5"
    >
      <div>
        <p class="text-sm text-fg-muted font-normal mb-2 text-center">
          You will deposit exactly
        </p>
        <p class="text-black text-2xl font-bold text-center">
          {{
            parseMoneyAll(
              assetDepositDetails.amount,
              assetDepositDetails.currency
            )
          }}
        </p>
      </div>
    </div>
    <div
      class="mt-5 border-style rounded-lg flex items-center justify-center py-5 px-6"
    >
      <div>
        <div class="mt-5">
          <div class="flex items-center justify-between gap-x-28 px-5">
            <div class="flex items-start gap-x-2">
              <currency-icons
                :currency="getCurrencyCountry(assetDepositDetails.currency)"
              />
              <div>
                <p class="text-xs font-normal text-fg-muted">Asset</p>
                <p class="text-black font-medium text-sm">
                  {{ assetDepositDetails.currency }}
                </p>
              </div>
            </div>
            <div class="flex items-start gap-x-2">
              <network-icons
                :currency="getNetworkFullName(assetDepositDetails.network)"
              />
              <div>
                <p class="text-xs font-normal text-fg-muted">Network</p>
                <p class="text-black font-medium text-sm">
                  {{ assetDepositDetails.network }}
                </p>
              </div>
            </div>
          </div>
          <div>
            <div v-if="isConfirming" class="mt-5 flex flex-col items-center">
              <circle-icon />
              <p class="mt-5 text-black text-xl font-bold">
                Confirming Deposit
              </p>
              <p class="mt-3 text-primary text-3xl">
                {{ `${dispMinutes}:${dispSeconds}` }}
              </p>
              <p
                style="word-break: normal"
                class="mt-5 text-sm text-fg-muted font-normal text-center mx-10"
              >
                This page would close once deposit is confirmed.
              </p>
            </div>
            <div v-if="isSuccessful" class="mt-5 flex flex-col items-center">
              <check-mark-icon class="p-0" />
              <p class="mt-5 text-black text-xl font-bold">Deposit Received</p>
              <p class="text-black text-base font-normal mt-3">
                We received
                <span class="font-bold">{{
                  parseMoneyAll(
                    assetDepositDetails.amount,
                    assetDepositDetails.currency
                  )
                }}</span>
              </p>
              <p class="mt-5 text-fg-muted font-normal text-sm">
                This transfer is now processing
              </p>
            </div>
            <div class="mt-5" v-if="isFailed">
              <div class="flex flex-col items-center justify-center">
                <primary-warn-icon />
                <h3 class="text-root-2 text-xl mt-4 mb-2">Deposit Time Out</h3>
                <p
                  style="word-break: normal"
                  class="text-sm font-light text-center"
                >
                  We did not receive your deposit because it expired.
                </p>
              </div>
              <div
                class="mt-8 w-full flex items-center gap-x-4 text-sm font-normal"
              >
                <div
                  class="w-1/2 bg-white border border-primary text-primary py-3.5 rounded-md cursor-pointer text-center"
                  @click="disputeHandler"
                >
                  Dispute
                </div>
                <div
                  class="w-1/2 bg-primary text-white py-3.5 rounded-md cursor-pointer text-center"
                  @click="retryHandler"
                >
                  Retry
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import {
  GetCurrencyCountry,
  ParseMoneyAll,
  GetNetworkFullName
} from '@/utils/helpers.js'
import getPendingDeposits from '@/mixins/pendingDeposit.js'
export default {
  mixins: [getPendingDeposits],
  data () {
    return {
      depositState: 'confirming',
      minutes: 0,
      seconds: 59,
      detailsInterval: '',
      timerInterval: ''
    }
  },
  components: {
    CurrencyIcons: () => import('@/assets/icons/CurrencyIcons.vue'),
    NetworkIcons: () => import('@/assets/v2/icons/NetworkIcons.vue'),
    CircleIcon: () => import('@/assets/icons/CircleIcon.vue'),
    CheckMarkIcon: () => import('@/assets/v2/icons/CheckMarkIcon.vue'),
    PrimaryWarnIcon: () => import('@/assets/v2/icons/PrimaryWarnIcon.vue')
  },
  methods: {
    getCurrencyCountry (val) {
      return GetCurrencyCountry(val)
    },
    getNetworkFullName (val) {
      return GetNetworkFullName(val)
    },
    parseMoneyAll (amount, currency) {
      return ParseMoneyAll(amount, currency)
    },
    retryHandler () {
      // Reset the countdown timer
      this.minutes = 0
      this.seconds = 59

      // Trigger the deposit confirmation process
      this.confirmDeposit()
    },
    async confirmDeposit () {
      this.setDepositState('confirming')

      const payload = {
        page: 1,
        size: 10,
        status: 'pending',
        type: 'asset_deposit',
        from: '',
        to: ''
      }

      let secondsElapsed = 0
      const interval = setInterval(async () => {
        await this.getDeposits(payload)

        if (
          this.depositedAmountDetails.amount.amount ===
          this.assetDepositDetails.amount
        ) {
          clearInterval(interval)
          this.setDepositState('success')
        }

        secondsElapsed += 10

        if (secondsElapsed >= 60) {
          clearInterval(interval)
          if (this.depositState !== 'success') {
            this.setDepositState('failed')
          }
        }
      }, 10000)

      this.countDown()
    },
    countDown () {
      // Set up a timer to decrement every second
      this.timerInterval = setInterval(() => {
        this.seconds--
        if (this.seconds < 1) {
          // If countdown reaches 0, mark the deposit as 'failed'
          clearInterval(this.timerInterval)
          this.setDepositState('failed')
        }
      }, 1000)
    },
    disputeHandler () {
      window.open('https://ovalfi.atlassian.net/servicedesk/customer/portals')
      this.$emit('handleClose')
    },
    setDepositState (state) {
      this.depositState = state
    }
  },
  mounted () {
    this.confirmDeposit()
  },
  beforeDestroy () {
    clearInterval(this.detailsInterval)
    clearInterval(this.timerInterval)
  },
  computed: {
    isConfirming () {
      return this.depositState === 'confirming'
    },
    isFailed () {
      return this.depositState === 'failed'
    },
    isSuccessful () {
      return this.depositState === 'success'
    },
    assetDepositDetails () {
      return this.$store.state.transactions.assetDepositDetails
    },
    dispMinutes () {
      return this.minutes
    },
    dispSeconds () {
      return this.seconds
    },
    transferDetails () {
      return (
        this.$store.state.terminal.transferTerminalDetails.transferDetails || {}
      )
    },
    pendingAssetDeposits () {
      return this.$store.state.transactions.deposits.items
    },
    depositedAmountDetails () {
      const depositDetails = this.pendingAssetDeposits.find(
        (deposit) =>
          deposit.amount.currency === this.assetDepositDetails.currency
      )
      return depositDetails
    }
  }
}
</script>

<style scoped>
.border-style {
  border: 1px solid var(--border-main);
}
</style>
